import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Form, Spinner } from "react-bootstrap";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import {
  errorMessage,
  getClaimBadges,
  getRuleJobs,
  getRulesReport,
  isAbortError,
  listEntityRows,
  listUsers,
} from "../../services/apiClient.js";
import JobList, { WFM_COLUMNS, WFM_ATTENTION_FLOOR } from "../claims/JobList.jsx";
import MarketingCallsPanel from "./MarketingCallsPanel.jsx";
import RulesSummary from "./RulesSummary.jsx";
import { rdrDefault } from "../../services/pageDefaults.js";

// The RDR counter-line report, generalised over a rule GROUP (workflow / main /
// accounts): one line per worklist in the legacy voice ("There are N …"), red
// count when it needs attention, dollar total when the rule carries money, and
// Show Me expanding the uniform JobList inline. Rules live in
// services/workflowReport.js (the badge engine's foundation) — this component
// only renders them.

const money = (v) =>
  v == null ? null : v.toLocaleString("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 });

// The sentence stays quiet; only the COUNT wears danger red (Robert's call —
// the whole line in red read as shouting).
//
// Grey means "nothing here", which is right for a rule that is clear. The three
// accepted-work readouts never alert by design, so they were permanently grey
// and read as switched off next to everything else — "There are 47 jobs waiting
// to be organised" is a real number, not an absence (Karen Sweatman 2026-08-12).
//
// A counted line looks the same whether or not it is a fault: same sentence,
// same red number (Robert 2026-08-12, having seen it rendered both ways). The
// red is doing "here is the figure", not "this is wrong" — `alert` still carries
// the fault meaning everywhere it matters, badges and Needs Attention included,
// and deliberately isn't widened to cover readouts just to colour a number.
function Message({ line }) {
  const counted = line.alert || (line.informational && line.count > 0);
  if (!counted) return <span className="text-muted">{line.message}</span>;
  const num = String(line.count);
  const idx = line.message.indexOf(num);
  if (idx === -1) return <span>{line.message}</span>;
  return (
    <span>
      {line.message.slice(0, idx)}
      <strong className="text-danger">{num}</strong>
      {line.message.slice(idx + num.length)}
    </span>
  );
}

// The two "still needing something" lists (workflow.md F.8). They are the only
// reports whose subject is the ABSENCE of other badges, so they show every
// group's chips rather than just their own — see the JobList call below.
const FINISH_RULES = new Set(["completed_not_finished", "declined_not_finished"]);

// A row on those lists carrying NO badges is one that has nothing left in its
// way, so the only thing standing between it and Finished is somebody saying so.
// Give it a chip that says exactly that.
//
// This only became meaningful once jobs stopped finishing themselves (Robert,
// 15 Aug: "a job should NEVER auto finish"). Before that, a row with no badges
// was a job the sweep was about to close on its own, so a chip would have been
// announcing something that was already happening. Now it is the one actionable
// state on the list, and everything else on the row is work to do first.
//
// Derived here rather than added as a rule because "nothing outstanding" is the
// negation of forty-eight WHERE clauses — not a thing to express in SQL when the
// badge sidecar has already answered it.
const SET_TO_FINISH = {
  key: "set_to_finish",
  label: "Set To Finish",
  group: "main",
  order: -1, // ahead of everything: it is the action, not another problem
  tab: "details",
};
function withSetToFinish(rows, map) {
  const out = { ...map };
  for (const row of rows) if (!(out[row.id] || []).length) out[row.id] = [SET_TO_FINISH];
  return out;
}

function WorkflowLine({ group, line, params, bulk = null, defaultOpen = false, onOpenChange = null, dateRange = null }) {
  const [open, setOpen] = useState(defaultOpen && line.count > 0);
  const [jobs, setJobs] = useState(null);
  const [badges, setBadges] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  // "Show all" opens every line at once, so one click can put ~19 of these in
  // flight. Cancel on unmount (a filter change re-keys the whole list) so the
  // ones nobody is waiting for stop competing with the ones they are.
  const abortRef = useRef(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  async function loadJobs() {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setLoadError("");
    try {
      const result = await getRuleJobs(group, line.key, params, { signal: controller.signal });
      setJobs(result);
      // Sidecar: every badge these jobs carry, including this rule's own.
      getClaimBadges(result.rows.map((r) => r.id), { signal: controller.signal })
        .then((map) => setBadges(FINISH_RULES.has(line.key) ? withSetToFinish(result.rows, map) : map))
        .catch(() => {});
    } catch (err) {
      if (isAbortError(err)) return;
      // Previously this set an EMPTY list, so a failed request was indis-
      // tinguishable from "no jobs here" — the one answer this report must
      // never give wrongly. Say it failed, and let them ask again.
      setLoadError(errorMessage(err, "Unable to load these jobs."));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }

  function setOpenReported(value) {
    setOpen(value);
    if (onOpenChange) onOpenChange(line.key, value);
  }

  async function toggle() {
    if (open) return setOpenReported(false);
    setOpenReported(true);
    if (!jobs) await loadJobs();
  }

  // Restore an open list on mount (back-navigation: the list you had open
  // comes back — Karen's "really annoying" fix).
  useEffect(() => {
    if (defaultOpen && line.count > 0 && !jobs) loadJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show all / Hide all from the report header.
  useEffect(() => {
    if (!bulk || bulk.seq === 0) return;
    if (bulk.open && line.count > 0) {
      setOpenReported(true);
      if (!jobs) loadJobs();
    } else if (!bulk.open) {
      setOpenReported(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulk?.seq]);

  return (
    <div className={`workflow-rule py-2 border-bottom${open ? " is-open" : ""}`}>
      {/* The whole sentence is a click target (Robert: a shortcut that expands/
          contracts in place); the Show Me pill stays as the labelled control. */}
      <div
        className={`d-flex flex-wrap align-items-center justify-content-between column-gap-3 row-gap-1${line.count > 0 ? " rule-line-header" : ""}`}
        onClick={line.count > 0 ? toggle : undefined}
        role={line.count > 0 ? "button" : undefined}
      >
        <span className="d-inline-flex flex-wrap align-items-center column-gap-2">
          <Message line={line} />
          {line.total != null && line.count > 0 ? (
            <strong className="text-danger">{money(line.total)}</strong>
          ) : null}
          {/* Completed-date range on lines that support it (Karen r1 — Non
              Invoiced Jobs). Filters the count AND the Show-me list. */}
          {line.hasDateRange && dateRange ? (
            <span className="d-inline-flex align-items-center column-gap-1" onClick={(e) => e.stopPropagation()}>
              <Form.Control
                size="sm" type="date" style={{ width: 140 }} data-hint="Completed on/after"
                value={dateRange.from} onChange={(e) => dateRange.setFrom(e.target.value)}
              />
              <span className="text-muted small">to</span>
              <Form.Control
                size="sm" type="date" style={{ width: 140 }} data-hint="Completed on/before"
                value={dateRange.to} onChange={(e) => dateRange.setTo(e.target.value)}
              />
            </span>
          ) : null}
        </span>
        {line.count > 0 ? (
          <Button
            size="sm"
            variant={open ? "secondary" : "outline-secondary"}
            className="rounded-pill d-inline-flex align-items-center gap-1 px-3 py-0"
            style={{ fontSize: "0.78rem", lineHeight: 1.8 }}
            onClick={(e) => {
              e.stopPropagation(); // header click handles it; don't double-toggle
              toggle();
            }}
          >
            {open ? "Hide" : "Show me"}
            {open ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
          </Button>
        ) : null}
      </div>
      {open ? (
        loading ? (
          <div className="py-2"><Spinner size="sm" /> <span className="small text-muted">Loading…</span></div>
        ) : loadError ? (
          <Alert variant="danger" className="my-2 py-2 d-flex align-items-center justify-content-between gap-3 flex-wrap">
            <span className="small">{loadError}</span>
            <Button size="sm" variant="outline-danger" onClick={loadJobs}>Try again</Button>
          </Alert>
        ) : jobs ? (
          <div className="mt-2 mb-1">
            {/* Every RDR list shows only ITS OWN category's badges, extended
                (Robert 2026-07-25): the PCO Dashboard shows the green "main"
                badges, Accounts the amber accounts badges, etc. The Dashboard's
                rows also open the Progress tab on click.

                THE TWO FINISH LISTS ARE THE EXCEPTION, and they have to be. They
                ask "why is this job not finished yet", and the answer is very
                often another department's problem — job 76452 is held open by
                Overdue Payments, which is an accounts badge, so under the group
                filter its co-ordinator saw a row with no reason on it at all
                (Robert, 15 Aug). Showing every group here is the difference
                between a worklist and a puzzle. */}
            <JobList
              rows={jobs.rows}
              badges={badges}
              badgesExtended
              badgeGroup={FINISH_RULES.has(line.key) ? null : group}
              rowTab={group === "main" ? "progress" : undefined}
              aligned /* several lists stacked on one page — pin the varying columns (Karen 2026-08-03) */
              /* Workflow Management reads a different question: the RDR note in
                 its own editable column and the status with the date it was
                 reached, paid for by Division / Project Co / Created. */
              columns={group === "workflow" ? WFM_COLUMNS : undefined}
              attentionFloor={group === "workflow" ? WFM_ATTENTION_FLOOR : undefined}
            />
            {jobs.rows.length >= jobs.cap ? (
              <div className="small text-warning mt-1">Showing the first {jobs.cap} — narrow this down.</div>
            ) : null}
          </div>
        ) : null
      ) : null}
    </div>
  );
}

function WorkflowReport({ group = "workflow" }) {
  // The report's state survives navigating into a claim and BACK, and now also
  // survives moving BETWEEN sections — but only the part that should.
  //
  // TWO STORES, because the filter row and the page are different things
  // (Robert 2026-08-03). The row along the top — coordinator, division, view,
  // detail/summary — is "who and what I am looking at", and it means the same
  // on all four sections, so answering it once on the PCO Dashboard and losing
  // it on the way to Accounts is just the question being asked again. That goes
  // in ONE shared store.
  //
  // Everything else stays per section: the date range is about the lines on the
  // page you are on, and which Show-me lists were open (and where you had
  // scrolled to) obviously cannot travel to a different set of rules.
  //
  // v2: withRecordsOnly became the DEFAULT (Karen 2026-07-19) — key bumped so
  // sessions saved under v1 don't keep the old default alive. Both keys sit
  // under the `rdrReport:` prefix that clearSavedPageState() purges, so saving
  // new page defaults still wins immediately.
  const storeKey = `rdrReport:${group}:v2`;
  const sharedKey = "rdrReport:filters:v2";
  const readStore = (key) => {
    try {
      return JSON.parse(sessionStorage.getItem(key)) || null;
    } catch {
      return null;
    }
  };
  const [saved] = useState(() => readStore(storeKey));
  const [sharedSaved] = useState(() => readStore(sharedKey));

  const [lines, setLines] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [users, setUsers] = useState([]);
  // No saved session state → the user's RDR page defaults seed each filter
  // (baseline: a PCO lands on their own jobs, Only-With-Records, Detail).
  // Session choices are saved below and always take precedence next time.
  // The four that TRAVEL: seeded from the shared store, so whatever was chosen
  // on the last RDR section is already answered on this one.
  const [division, setDivision] = useState(sharedSaved?.division ?? rdrDefault("division")); // "" = All (legacy default radio)
  const [pm, setPm] = useState(sharedSaved?.pm ?? rdrDefault("pm")); // "" All | me | me+ | other
  const [pmUser, setPmUser] = useState(sharedSaved?.pmUser ?? "");
  const [withRecordsOnly, setWithRecordsOnly] = useState(sharedSaved?.withRecordsOnly ?? rdrDefault("withRecordsOnly")); // legacy View radios — DEFAULT "only with records" (Karen 2026-07-19)
  // Detail = the rule lines + Show-me lists; Summary = 1.0's per-person grid
  // (Karen 2026-07-19). Travels with the rest of the row it sits in.
  const [mode, setMode] = useState(sharedSaved?.mode ?? rdrDefault("mode"));
  // Date range for lines that support it (Non Invoiced Jobs completed-date).
  // PER SECTION deliberately: it belongs to the lines on this page, not to the
  // question the filter row is asking, so it does not follow you to Accounts.
  const [dateFrom, setDateFrom] = useState(saved?.dateFrom ?? "");
  const [dateTo, setDateTo] = useState(saved?.dateTo ?? "");
  const [openKeys, setOpenKeys] = useState(() => new Set(saved?.openKeys || []));
  // Summary has inline drill-downs too. Keep the opened cell per rule so Back
  // can rebuild the same view before restoring the scroll position.
  const [summaryOpenCells, setSummaryOpenCells] = useState(saved?.summaryOpenCells || {});
  const [summaryReady, setSummaryReady] = useState(false);
  const [bulk, setBulk] = useState({ seq: 0, open: false }); // Show all / Hide all signal
  const [error, setError] = useState("");
  const [reloadToken, setReloadToken] = useState(0); // bumped by "Try again"

  useEffect(() => {
    listEntityRows("divisions").then(setDivisions).catch(() => {});
    listUsers({ pco: true, activeOnly: true }).then(setUsers).catch(() => {});
  }, []);

  function handleOpenChange(key, isOpen) {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (isOpen) next.add(key);
      else next.delete(key);
      return next;
    });
  }

  function handleSummaryOpenChange(ruleKey, cell) {
    setSummaryOpenCells((prev) => {
      const next = { ...prev };
      if (cell) next[ruleKey] = cell;
      else delete next[ruleKey];
      return next;
    });
  }

  // Scroll restoration (best effort): remember where you were on this report,
  // and after coming BACK, return there once the lines (and any reopened
  // Show-me lists) have had a moment to render.
  const restoredScroll = useRef(false);
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        try {
          sessionStorage.setItem(`${storeKey}:scroll`, String(window.scrollY));
        } catch {
          /* ignore */
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [storeKey]);
  useEffect(() => {
    const contentReady = mode === "summary" ? summaryReady : !!lines;
    if (restoredScroll.current || !contentReady) return;
    restoredScroll.current = true;
    let y = 0;
    try {
      y = Number(sessionStorage.getItem(`${storeKey}:scroll`)) || 0;
    } catch {
      /* ignore */
    }
    if (y > 0) {
      // Twice: once now, once after the restored lists have loaded their rows.
      requestAnimationFrame(() => window.scrollTo(0, y));
      setTimeout(() => window.scrollTo(0, y), 500);
    }
  }, [lines, mode, storeKey, summaryReady]);

  // The filter row, written to the SHARED store so the next section starts where
  // this one left off.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        sharedKey,
        JSON.stringify({ pm, pmUser, division, withRecordsOnly, mode })
      );
    } catch {
      /* private mode etc. */
    }
  }, [sharedKey, pm, pmUser, division, withRecordsOnly, mode]);

  // This page's own state, which stays with this page.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        storeKey,
        JSON.stringify({ dateFrom, dateTo, openKeys: [...openKeys], summaryOpenCells })
      );
    } catch {
      /* private mode etc. */
    }
  }, [storeKey, dateFrom, dateTo, openKeys, summaryOpenCells]);

  const params = { division, pm, pm_user: pm === "other" ? pmUser : "", from: dateFrom, to: dateTo };
  const paramsKey = `${division}:${pm}:${pm === "other" ? pmUser : ""}:${dateFrom}:${dateTo}`;

  // Cancelled on unmount and on every filter change — clicking between sections
  // faster than the answers come back must not leave the old requests running,
  // nor let a late one overwrite the current view. See RulesSummary.
  useEffect(() => {
    if (pm === "other" && !pmUser) return undefined; // wait for a PM pick
    if (mode === "summary") return undefined; // RulesSummary fetches its own data
    const controller = new AbortController();
    setLines(null);
    setError("");
    getRulesReport(group, params, { signal: controller.signal })
      .then(setLines)
      .catch((err) => {
        if (isAbortError(err)) return;
        setError(errorMessage(err, "Unable to load the report."));
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, paramsKey, mode, reloadToken]);

  // An error here blanks the whole page, filters and all, so it must offer a
  // way back — before this, a stalled report meant navigating away and in again.
  if (error) {
    return (
      <Alert variant="danger" className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
        <span>{error}</span>
        <Button size="sm" variant="outline-danger" onClick={() => setReloadToken((n) => n + 1)}>
          Try again
        </Button>
      </Alert>
    );
  }

  const shown = lines ? (withRecordsOnly ? lines.filter((l) => l.count > 0) : lines) : null;

  return (
    <Card body className="workflow-worklist">
      {/* The legacy RDR filter bar: Project Co-Ordinator · Division · View */}
      <div className="workflow-filters d-flex flex-wrap align-items-center column-gap-4 row-gap-1 pb-2 border-bottom">
        <div className="d-flex flex-wrap align-items-center column-gap-2">
          <span className="text-uppercase fw-semibold small text-muted" style={{ letterSpacing: "0.06em" }}>
            Project Co-Ordinator
          </span>
          {[["me", "Me"], ["me+", "Me +"], ["", "All"], ["other", "Other"]].map(([v, label]) => (
            <Form.Check
              inline type="radio" key={v || "all"} id={`${group}-pm-${v || "all"}`} name={`${group}-pm`}
              label={label} checked={pm === v} onChange={() => setPm(v)} className="me-0"
            />
          ))}
          {pm === "other" ? (
            <ThemedSelect size="sm" value={pmUser} onChange={(e) => setPmUser(e.target.value)} style={{ width: 170 }}>
              <option value="">— Choose PCO —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.full_name}</option>
              ))}
            </ThemedSelect>
          ) : null}
        </div>

        <div className="d-flex flex-wrap align-items-center column-gap-2">
          <span className="text-uppercase fw-semibold small text-muted" style={{ letterSpacing: "0.06em" }}>
            Division
          </span>
          <Form.Check
            inline type="radio" id={`${group}-div-all`} name={`${group}-division`} label="All" className="me-0"
            checked={division === ""} onChange={() => setDivision("")}
          />
          {divisions.map((d) => (
            <Form.Check
              inline type="radio" key={d.id} id={`${group}-div-${d.id}`} name={`${group}-division`} className="me-0"
              label={d.name} checked={String(division) === String(d.id)}
              onChange={() => setDivision(String(d.id))}
            />
          ))}
        </div>

        <div className="d-flex flex-wrap align-items-center column-gap-2">
          <span className="text-uppercase fw-semibold small text-muted" style={{ letterSpacing: "0.06em" }}>
            View
          </span>
          <Form.Check
            inline type="radio" id={`${group}-view-all`} name={`${group}-view`} label="All Reports" className="me-0"
            checked={!withRecordsOnly} onChange={() => setWithRecordsOnly(false)}
          />
          <Form.Check
            inline type="radio" id={`${group}-view-records`} name={`${group}-view`} label="Only With Records" className="me-0"
            checked={withRecordsOnly} onChange={() => setWithRecordsOnly(true)}
          />
        </div>

        {/* Detail vs the 1.0-style per-person Summary grid (Karen 2026-07-19). */}
        <div className="d-flex flex-wrap align-items-center column-gap-2">
          <span className="text-uppercase fw-semibold small text-muted" style={{ letterSpacing: "0.06em" }}>
            Show
          </span>
          <Form.Check
            inline type="radio" id={`${group}-mode-detail`} name={`${group}-mode`} label="All Detail" className="me-0"
            checked={mode === "detail"} onChange={() => setMode("detail")}
          />
          <Form.Check
            inline type="radio" id={`${group}-mode-summary`} name={`${group}-mode`} label="All Summary" className="me-0"
            checked={mode === "summary"} onChange={() => setMode("summary")}
          />
        </div>

        {/* Show all = every line's jobs in one hit (Karen). */}
        <div className="ms-auto d-flex column-gap-2" style={mode === "summary" ? { visibility: "hidden" } : undefined}>
          <Button
            size="sm" variant="outline-secondary" className="rounded-pill py-0"
            style={{ fontSize: "0.78rem", lineHeight: 1.8 }}
            onClick={() => {
              setOpenKeys(new Set((lines || []).filter((l) => l.count > 0).map((l) => l.key)));
              setBulk((b) => ({ seq: b.seq + 1, open: true }));
            }}
          >
            Show all
          </Button>
          <Button
            size="sm" variant="outline-secondary" className="rounded-pill py-0"
            style={{ fontSize: "0.78rem", lineHeight: 1.8 }}
            onClick={() => {
              setOpenKeys(new Set());
              setBulk((b) => ({ seq: b.seq + 1, open: false }));
            }}
          >
            Hide all
          </Button>
        </div>
      </div>

      {mode === "summary" ? (
        <RulesSummary
          group={group}
          params={params}
          paramsKey={paramsKey}
          withRecordsOnly={withRecordsOnly}
          openCells={summaryOpenCells}
          onOpenCellChange={handleSummaryOpenChange}
          onReady={() => setSummaryReady(true)}
        />
      ) : !shown ? (
        <p className="text-muted pt-2 mb-0">Loading report…</p>
      ) : shown.length === 0 ? (
        <p className="text-muted pt-2 mb-0">Nothing needs attention — every report is clear.</p>
      ) : (
        shown.map((line) => (
          // Keyed by the filters so open lists refetch under the new filter.
          <WorkflowLine
            key={`${paramsKey}:${line.key}`}
            group={group}
            line={line}
            params={params}
            bulk={bulk}
            defaultOpen={openKeys.has(line.key)}
            onOpenChange={handleOpenChange}
            dateRange={line.hasDateRange ? { from: dateFrom, to: dateTo, setFrom: setDateFrom, setTo: setDateTo } : null}
          />
        ))
      )}
      {/* 1.0's WFM page carries the marketing chase; the panel self-gates on
          the marketing_calls_enabled setting (Robert 2026-07-17 — the toggle
          governs, so WFM shows it whenever marketing is on, like 1.0). */}
      {group === "workflow" ? <MarketingCallsPanel /> : null}
    </Card>
  );
}

export default WorkflowReport;
